/*
Copyright (C) 2019  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { FileCode } from "lucide-react";

import { UserScriptData, UserScriptValue } from "@src/@types/MainItem";
import {
  InstanceImage,
  OsPill,
} from "@src/components/modules/WizardModule/WizardInstances";
import {
  SectionHeading,
  Spinner,
  T9,
} from "@src/components/modules/WizardModule/WizardKit";
import { ActionButton } from "@src/components/ui/DetailsKit/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import InfoIcon from "@src/components/ui/InfoIcon";
import Modal from "@src/components/ui/Modal";

import UserScriptsModal, { ScriptsByPhase } from "./UserScriptsModal";

import type {
  Instance,
  InstanceScript,
  UserScriptTarget,
} from "@src/@types/Instance";
import {
  DEFAULT_USER_SCRIPT_PHASE,
  USER_SCRIPT_PHASES,
  UserScriptPhase,
} from "@src/@types/Instance";

const parseScriptValueByPhase = (value: UserScriptValue): ScriptsByPhase => {
  const map: ScriptsByPhase = {};
  if (!value) {
    return map;
  }
  if (typeof value === "string") {
    map[DEFAULT_USER_SCRIPT_PHASE] = { content: value, fileName: null };
    return map;
  }
  value.forEach(item => {
    map[item.phase || DEFAULT_USER_SCRIPT_PHASE] = {
      content: item.payload,
      fileName: null,
    };
  });
  return map;
};

const Wrapper = styled.div<any>`
  width: 100%;
  display: flex;
  overflow: auto;
  flex-direction: column;
  min-height: 0;
`;
const Group = styled.div<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const Heading = styled(SectionHeading)<any>`
  gap: 0;
`;
const InfoIconStyled = styled(InfoIcon)<any>`
  margin-left: 8px;
`;
const Scripts = styled.div<any>`
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
`;
const Script = styled.div<any>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  border-top: 1px solid ${T9.slate200};
  padding: 10px 16px;

  &:first-child {
    border-top: none;
  }
`;
const Name = styled.div<any>`
  display: flex;
  align-items: center;
  min-width: 0;
`;
const NameLabel = styled.div<any>`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
  min-width: 0;
`;
const NameLabelTitle = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${T9.slate900};
  word-break: break-word;
`;
const NameLabelSubtitle = styled.div<any>`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
  margin-top: 2px;
  word-break: break-word;
`;
const Configured = styled.span`
  font-size: 12px;
  color: var(--t9-success-700);
  font-weight: 500;
`;
const LinkButton = styled(ActionButton)`
  flex-shrink: 0;
  margin-left: 16px;
`;
type ModalTarget = UserScriptTarget & { title: string };

type Props = {
  instances: Instance[];
  uploadedScripts: InstanceScript[];
  removedScripts: InstanceScript[];
  layout?: "modal" | "page";
  loadingInstances?: boolean;
  userScriptData: UserScriptData | null | undefined;
  style?: React.CSSProperties;
  onScrollableRef?: (ref: HTMLElement) => void;
  scrollableRef?: (r: HTMLElement) => void;
  onScriptsChange: (
    target: UserScriptTarget,
    scripts: InstanceScript[],
    hadExisting: boolean,
  ) => void;
};
type State = {
  modalTarget: ModalTarget | null;
};
@observer
class WizardScripts extends React.Component<Props, State> {
  state: State = {
    modalTarget: null,
  };

  matchesTarget(script: InstanceScript, target: UserScriptTarget): boolean {
    return target.global
      ? script.global === target.global
      : script.instanceId === target.instanceId;
  }

  getBaseValue(target: UserScriptTarget): UserScriptValue {
    if (target.global) {
      return this.props.userScriptData?.global?.[target.global] ?? null;
    }
    if (target.instanceId) {
      return this.props.userScriptData?.instances?.[target.instanceId] ?? null;
    }
    return null;
  }

  getScriptsByPhase(target: UserScriptTarget): ScriptsByPhase {
    const uploaded = this.props.uploadedScripts.filter(s =>
      this.matchesTarget(s, target),
    );
    const isRemoved = this.props.removedScripts.some(s =>
      this.matchesTarget(s, target),
    );
    if (uploaded.length || isRemoved) {
      const map: ScriptsByPhase = {};
      uploaded.forEach(s => {
        map[s.phase || DEFAULT_USER_SCRIPT_PHASE] = {
          content: s.scriptContent,
          fileName: s.fileName,
        };
      });
      return map;
    }
    return parseScriptValueByPhase(this.getBaseValue(target));
  }

  getConfiguredPhases(target: UserScriptTarget): UserScriptPhase[] {
    const map = this.getScriptsByPhase(target);
    return USER_SCRIPT_PHASES.filter(phase => map[phase]?.content);
  }

  computeHadExisting(target: UserScriptTarget): boolean {
    const baseMap = parseScriptValueByPhase(this.getBaseValue(target));
    return USER_SCRIPT_PHASES.some(phase => baseMap[phase]?.content);
  }

  handleModalSave(target: ModalTarget, scripts: InstanceScript[]) {
    this.props.onScriptsChange(
      { global: target.global, instanceId: target.instanceId },
      scripts,
      this.computeHadExisting(target),
    );
    this.setState({ modalTarget: null });
  }

  renderScriptItem(opts: {
    global?: "windows" | "linux";
    instanceId?: string;
    title: string;
    subtitle?: string;
    os?: string | null;
  }) {
    const { global, instanceId, title, subtitle, os } = opts;
    const target: UserScriptTarget = {
      global: global ?? null,
      instanceId: instanceId ?? null,
    };
    const configured = this.getConfiguredPhases(target).length;
    const isConfigured = configured > 0;

    return (
      <Script key={title}>
        <Name>
          {global ? (
            <IconTile icon={FileCode} size={36} tone="brand" />
          ) : (
            <InstanceImage os={os || undefined} />
          )}
          <NameLabel>
            <NameLabelTitle>
              <span>{title}</span>
              {os ? <OsPill os={os} /> : null}
              {isConfigured ? (
                <Configured>
                  {configured} script{configured === 1 ? "" : "s"}
                </Configured>
              ) : null}
            </NameLabelTitle>
            {subtitle ? (
              <NameLabelSubtitle>{subtitle}</NameLabelSubtitle>
            ) : null}
          </NameLabel>
        </Name>
        <LinkButton
          type="button"
          variant="secondary"
          onClick={() => {
            this.setState({ modalTarget: { ...target, title } });
          }}
        >
          {isConfigured ? "Edit scripts" : "Choose scripts"}
        </LinkButton>
      </Script>
    );
  }

  renderScriptGroup(group: "global" | "instance") {
    if (group === "global") {
      return (
        <Group>
          <Heading layout={this.props.layout}>
            Global scripts
            <InfoIconStyled
              layout={this.props.layout}
              text="User scripts that run during OS morphing on every VM of an OS type. You can attach one script per phase."
            />
          </Heading>
          <Scripts>
            {this.renderScriptItem({
              global: "windows",
              title: "Windows VMs",
            })}
            {this.renderScriptItem({
              global: "linux",
              title: "Linux VMs",
            })}
          </Scripts>
        </Group>
      );
    }

    if (this.props.instances.length === 0 && !this.props.loadingInstances) {
      return null;
    }

    return (
      <Group layout={this.props.layout}>
        <Heading layout={this.props.layout}>
          VM scripts
          {!this.props.loadingInstances ? (
            <InfoIconStyled
              layout={this.props.layout}
              text="User scripts that run during OS morphing on one VM. You can attach one script per phase. They override the global scripts."
            />
          ) : null}
          {this.props.loadingInstances ? (
            <Spinner size={16} style={{ marginLeft: 8 }} />
          ) : null}
        </Heading>
        <Scripts>
          {this.props.instances.map(instance => {
            const id = instance.instance_name || instance.id;
            const title = instance.name;
            const knownOs =
              instance.os_type === "windows" || instance.os_type === "linux";
            const osType =
              instance.os_type && !knownOs ? `${instance.os_type} · ` : "";
            const subtitle = `${osType}${instance.num_cpu} vCPU · ${instance.memory_mb} MB RAM`;

            return this.renderScriptItem({
              instanceId: id,
              title,
              subtitle,
              os: knownOs ? instance.os_type : null,
            });
          })}
        </Scripts>
      </Group>
    );
  }

  render() {
    const { modalTarget } = this.state;
    return (
      <Wrapper
        style={this.props.style}
        ref={(r: HTMLElement) => {
          if (this.props.onScrollableRef) {
            this.props.onScrollableRef(r);
          }
        }}
      >
        {this.renderScriptGroup("global")}
        {this.renderScriptGroup("instance")}
        <Modal
          isOpen={Boolean(modalTarget)}
          title={modalTarget ? `User scripts: ${modalTarget.title}` : ""}
          contentWidth={576}
          onRequestClose={() => this.setState({ modalTarget: null })}
        >
          {modalTarget ? (
            <UserScriptsModal
              title={modalTarget.title}
              global={modalTarget.global}
              instanceId={modalTarget.instanceId}
              scriptsByPhase={this.getScriptsByPhase(modalTarget)}
              onRequestClose={() => this.setState({ modalTarget: null })}
              onSave={scripts => this.handleModalSave(modalTarget, scripts)}
            />
          ) : null}
        </Modal>
      </Wrapper>
    );
  }
}

export default WizardScripts;
